import {
  Box,
  Flex,
  Heading,
  IconButton,
  Tag,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useState } from "react";
import { RiArrowDownSLine, RiDeleteBinLine } from "react-icons/ri";
import { Card } from "src/components/Layout/Card";
import { ConditionalTooltip } from "src/components/Layout/ConditionalTooltip";
import { maybePluralize } from "src/services/format";
import {
  seatTypeLabel,
  type OfferingOption,
  type SchoolOffering,
  type SeatType,
} from "../offerings";
import { AddOfferingButton } from "./AddOfferingButton";
import { AddSeatTypeButton } from "./AddSeatTypeButton";

const CHEVRON_COLUMN_WIDTH = 10;

type Props = {
  schoolId: string;
  schoolName: string;
  offerings: SchoolOffering[];
  options: OfferingOption[];
  isExpanded: boolean;
  showSeatTypes: boolean;
  deletingOfferingId: string | null;
  deletingSeatTypeId: string | null;
  onDeleteOffering: (offeringId: string) => void;
  onDeleteSeatType: (offeringId: string, seatTypeId: string) => void;
  onAddOffering: (optionIds: string[]) => void;
  onAddSeatType: (offeringId: string, optionId: string | null) => void;
  onGoToOfferingCategories: () => void;
  onToggleExpanded: () => void;
};

export const SchoolOfferingsCard: React.FC<Props> = ({
  schoolId,
  schoolName,
  offerings,
  options,
  isExpanded,
  showSeatTypes,
  deletingOfferingId,
  deletingSeatTypeId,
  onDeleteOffering,
  onDeleteSeatType,
  onAddOffering,
  onAddSeatType,
  onGoToOfferingCategories,
  onToggleExpanded,
}) => {
  const offeringCount = offerings.length;

  return (
    <Card
      display="flex"
      flexDirection="column"
      showBorder
      px={0}
      py={2}
      width="100%"
    >
      <Flex
        align="center"
        gap={2}
        width="100%"
        pt={2}
        pb={isExpanded ? 4 : 2}
        borderBottomWidth={isExpanded ? "1px" : undefined}
        borderBottomColor="gray.300"
      >
        <Box
          w={CHEVRON_COLUMN_WIDTH}
          pl={2}
          flexShrink={0}
          display="flex"
          justifyContent="center"
        >
          <IconButton
            aria-label={isExpanded ? "Collapse offerings" : "Expand offerings"}
            aria-expanded={isExpanded}
            icon={<RiArrowDownSLine />}
            variant="ghost"
            transform={isExpanded ? undefined : "rotate(-90deg)"}
            onClick={onToggleExpanded}
          />
        </Box>
        <VStack align="start" flex={1} gap={0}>
          <Heading fontSize="lg">{schoolName}</Heading>
          <Text fontSize="sm" color="gray.500">
            {schoolId}
          </Text>
        </VStack>
        <Flex align="center" gap={4} mx={4}>
          <Tag
            size="sm"
            colorScheme="gray"
            variant={offeringCount === 0 ? "outline" : "subtle"}
            color={offeringCount === 0 ? "gray.500" : undefined}
            boxShadow={
              offeringCount === 0
                ? "inset 0 0 0 1px var(--chakra-colors-gray-300)"
                : undefined
            }
          >
            {offeringCount === 0
              ? "Empty"
              : `${offeringCount} ${maybePluralize(offeringCount, "offering")}`}
          </Tag>
        </Flex>
      </Flex>
      {isExpanded && (
        <>
          {offerings.map((offering) => (
            <OfferingRow
              key={offering.id}
              offering={offering}
              options={options}
              showSeatTypes={showSeatTypes}
              isDeleting={deletingOfferingId === offering.id}
              deletingSeatTypeId={deletingSeatTypeId}
              onDeleteOffering={() => onDeleteOffering(offering.id)}
              onDeleteSeatType={(seatTypeId) =>
                onDeleteSeatType(offering.id, seatTypeId)
              }
              onAddSeatType={(optionId) => onAddSeatType(offering.id, optionId)}
              onGoToOfferingCategories={onGoToOfferingCategories}
            />
          ))}
          <Flex align="center" width="100%" mt={2}>
            <Box w={CHEVRON_COLUMN_WIDTH} flexShrink={0} />
            <AddOfferingButton
              options={options}
              existingOfferings={offerings}
              onAddOffering={onAddOffering}
              onGoToOfferingCategories={onGoToOfferingCategories}
            />
          </Flex>
        </>
      )}
    </Card>
  );
};

const OfferingRow: React.FC<{
  offering: SchoolOffering;
  options: OfferingOption[];
  showSeatTypes: boolean;
  isDeleting: boolean;
  deletingSeatTypeId: string | null;
  onDeleteOffering: () => void;
  onDeleteSeatType: (seatTypeId: string) => void;
  onAddSeatType: (optionId: string | null) => void;
  onGoToOfferingCategories: () => void;
}> = ({
  offering,
  options,
  showSeatTypes,
  isDeleting,
  deletingSeatTypeId,
  onDeleteOffering,
  onDeleteSeatType,
  onAddSeatType,
  onGoToOfferingCategories,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <Box borderBottomWidth="1px" borderBottomColor="gray.300">
      <Flex
        align="center"
        gap={2}
        width="100%"
        py={1}
        borderBottomWidth={showSeatTypes && isExpanded ? "1px" : undefined}
        borderBottomColor="gray.300"
      >
        {showSeatTypes ? (
          <Box
            w={CHEVRON_COLUMN_WIDTH}
            flexShrink={0}
            display="flex"
            justifyContent="flex-end"
          >
            <IconButton
              aria-label={
                isExpanded ? "Collapse seat types" : "Expand seat types"
              }
              aria-expanded={isExpanded}
              icon={<RiArrowDownSLine />}
              variant="ghost"
              size="xs"
              transform={isExpanded ? undefined : "rotate(-90deg)"}
              onClick={() => setIsExpanded((current) => !current)}
            />
          </Box>
        ) : (
          <Box w={CHEVRON_COLUMN_WIDTH} flexShrink={0} pl={2} />
        )}
        <OfferingLabel options={offering.options} />
        <Flex align="center" mx={4} flexShrink={0}>
          <IconButton
            aria-label="Delete offering"
            icon={<RiDeleteBinLine />}
            size="xs"
            variant="outline"
            colorScheme="gray"
            isLoading={isDeleting}
            onClick={onDeleteOffering}
          />
        </Flex>
      </Flex>
      {showSeatTypes && isExpanded && (
        <Box bg="blackAlpha.50">
          {offering.seatTypes.map((seatType) => (
            <SeatTypeRow
              key={seatType.id}
              seatType={seatType}
              isLastSeatType={offering.seatTypes.length === 1}
              isDeleting={deletingSeatTypeId === seatType.id}
              onDelete={() => onDeleteSeatType(seatType.id)}
            />
          ))}
          <Flex align="center" width="100%" py={1}>
            <Box w={CHEVRON_COLUMN_WIDTH} flexShrink={0} ml={2} />
            <AddSeatTypeButton
              options={options}
              offering={offering}
              onAddSeatType={onAddSeatType}
              onGoToOfferingCategories={onGoToOfferingCategories}
            />
          </Flex>
        </Box>
      )}
    </Box>
  );
};

const SeatTypeRow: React.FC<{
  seatType: SeatType;
  isLastSeatType: boolean;
  isDeleting: boolean;
  onDelete: () => void;
}> = ({ seatType, isLastSeatType, isDeleting, onDelete }) => {
  return (
    <Flex
      align="center"
      gap={2}
      width="100%"
      py={1}
      borderBottomWidth="1px"
      borderBottomColor="gray.300"
    >
      <Box w={CHEVRON_COLUMN_WIDTH} flexShrink={0} />
      <Text flex={1} fontSize="xs">
        {seatTypeLabel(seatType)}
      </Text>
      <Flex align="center" mx={4} flexShrink={0}>
        <ConditionalTooltip
          showTooltip={isLastSeatType}
          label="At least one seat type is required. Add a new seat type before deleting this one."
          hasArrow
          placement="top"
        >
          <Box display="inline-flex">
            <IconButton
              aria-label="Delete seat type"
              icon={<RiDeleteBinLine />}
              size="xs"
              variant="outline"
              colorScheme="gray"
              bg="white"
              isDisabled={isLastSeatType}
              isLoading={isDeleting}
              onClick={onDelete}
            />
          </Box>
        </ConditionalTooltip>
      </Flex>
    </Flex>
  );
};

const OfferingLabel: React.FC<{ options: SchoolOffering["options"] }> = ({
  options,
}) => {
  const [firstOption, ...restOptions] = options;

  return (
    <Text flex={1} fontSize="xs">
      {firstOption != null && (
        <Text as="span" fontWeight="bold">
          {firstOption.label}
          {restOptions.length > 0 ? "," : ""}
        </Text>
      )}
      {restOptions.length > 0 && (
        <Text as="span">
          {` ${restOptions.map((option) => option.label).join(", ")}`}
        </Text>
      )}
    </Text>
  );
};
